import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import notFoundImg from "../Assets/images/notfound.webp";

import {
  getCategoryList,
  getListProductVariantsMax,
  getProductsList,
  getProductVariants,
  toastSuccess,
} from "../api/services";
import PriceFilter from "../Components/Category/PriceFilter";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, MAX_QUANTITY } from "../Features/Cart/cartSlice";
import Skeleton from "react-loading-skeleton";
import ProductCard from "../Components/Home/Limited/ProductCard";

const Category = () => {
  const { categorySlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [maxPrice, setMaxPrice] = useState(0);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [sortBy, setSortBy] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingProductId, setLoadingProductId] = useState(null);

  useEffect(() => {
    const loadListProductVariantsMax = async () => {
      try {
        const res = await getListProductVariantsMax();
        setMaxPrice(res?.data?.data || []);
      } catch (err) {
        console.error("Category error:", err);
      }
    };

    loadListProductVariantsMax();
  }, []);

  useEffect(() => {
    const loadProductVariants = async () => {
      try {
        const res = await getProductVariants();
        setAvailableSizes(res?.data?.data || []);
      } catch (err) {
        console.error("Category error:", err);
      }
    };

    loadProductVariants();
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await getCategoryList();
        setCategories(res?.data?.data || []);
      } catch (err) {
        console.error("Category error:", err);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    if (!categories.length) return;

    const fetchProducts = async () => {
      setLoading(true);

      try {
        const selectedCategory = categories.find(
          (c) => c.slug === categorySlug,
        );

        const params = {
          category: selectedCategory?.id || "",
          page: currentPage,
          sort: sortBy,
          price: `${priceRange.min}-${priceRange.max}`,
          sizes: selectedSizes.join(","),
        };

        const res = await getProductsList(params);

        setProducts(res?.data?.data?.products || []);
        setTotalPages(res?.data?.data?.totalPages || 1);
      } catch (err) {
        console.error("Product error:", err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [
    categories,
    categorySlug,
    currentPage,
    sortBy,
    priceRange,
    selectedSizes,
  ]);

  const handleSizeToggle = (size) => {
    setSelectedSizes((prev) =>
      prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size],
    );
    setCurrentPage(1);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setCurrentPage(1);
  };

  const dispatch = useDispatch();

  const cartItems = useSelector((state) => state.cart.items);

  const handleAddToCart = async (data) => {
    
    const qtyToAdd = 1;
    if (qtyToAdd < 1) {
      toastSuccess("Quantity must be at least 1");
      return;
    }

    const minDelay = new Promise((resolve) => setTimeout(resolve, 1000));
    setLoadingProductId(data.id);

    try {
      const productDetails = {
        productID: data.id,
        variantID: data?.product_variants[0].id,
        productName: data.name,
        discount:data?.product_variants[0].discount,
        discount_amount:data?.product_variants[0].discount_amount,
        price:data?.product_variants[0].price,
        productPrice: data?.product_variants[0].final_price,
        frontImg: data?.product_variants[0].images?.[0] || data.image,
        productReviews: "8k+ reviews",
        quantity: qtyToAdd,
      };

      const existingItem = cartItems.find(
        (item) => item.variantID === data?.product_variants[0].id,
      );
      const currentQty = existingItem ? existingItem.quantity : 0;
      const newTotal = currentQty + qtyToAdd;

      if (newTotal > MAX_QUANTITY) {
        toastSuccess(`You can only add up to ${MAX_QUANTITY} of this variant`);
        return;
      }

      dispatch(addToCart(productDetails));
      toastSuccess(`${qtyToAdd} item(s) added to cart!`);
    } catch (error) {
      console.error("Add to cart error:", error);
      toastSuccess("Failed to add to cart. Please try again.");
    } finally {
      await minDelay;
      setLoadingProductId(null);
    }
  };

  const renderProduct = (product) => {
    return (
      <div className="col-lg-4 col-md-6 col-12" key={product.id}>
        <ProductCard
          product={product}
          onAddToCart={handleAddToCart}
          isLoading={loadingProductId === product.id}
        />
      </div>
    );
  };

  // Pagination controls
  const Pagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => setCurrentPage(i)}>
            {i}
          </button>
        </li>,
      );
    }
    return (
      <ul className="pagination justify-content-center">
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button
            className="page-link"
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </button>
        </li>
        {pages}
        <li
          className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </button>
        </li>
      </ul>
    );
  };

  const formatText = (text) => {
    if (!text || typeof text !== "string") return "";

    return text
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="product_category_page main__wrapper">
      <div className="product_category_content content__page">
        <div className="product_category_main_sidebar_wrap">
          <div className="container">
            <div className={`row main-content-wrap`}>
              {/* Sidebar */}
              <div className="col-lg-3 col-md-4 col-sm-4">
                <PriceFilter
                  onFilter={({ min, max }) => {
                    setPriceRange({ min, max });
                    setCurrentPage(1);
                  }}
                  max={maxPrice}
                />

                {/* Size filter (accordion) */}
                {availableSizes.length > 0 && (
                  <div className="product_color_widget pro-widget common-tabs">
                    <button
                      className="accordion pro-title"
                      onClick={() => setSizeOpen(!sizeOpen)}
                    >
                      Filter by Sizes
                    </button>

                    {sizeOpen && (
                      <div className="panel">
                        <ul>
                          {availableSizes.map((size) => (
                            <li key={size?.size_unit}>
                              <label>
                                <input
                                  type="checkbox"
                                  value={size?.size_unit}
                                  style={{
                                    marginRight: "10px",
                                    marginBottom: "2px",
                                  }}
                                  checked={selectedSizes.includes(
                                    size?.size_unit,
                                  )}
                                  onChange={() =>
                                    handleSizeToggle(size?.size_unit)
                                  }
                                />
                                {formatText(size?.size_unit)}
                              </label>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Category list */}
                <div className="product_cat_widget pro-widget">
                  <h3 className="pro-title">Product Category</h3>
                  <ul>
                    {categories.map((cat) => (
                      <li key={cat.id}>
                        <Link to={`/category/${cat.slug}`}>{cat.name}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Main content */}
              <div className="col-lg-9 col-md-8 col-sm-8">
                <div className="product_category_rightbar">
                  <div className="d-flex heading_line align-items-center">
                    <h3 className="content-title theme_color">
                      {formatText(
                        categorySlug
                          ? categorySlug.replace(/-/g, " ")
                          : "Products",
                      )}
                    </h3>
                    <ul className="breadcrumb-1 ml-auto d-flex">
                      <li>
                        <div className="filter-btn">
                          <select
                            className="form-control"
                            value={sortBy}
                            onChange={handleSortChange}
                          >
                            <option value="">Sort By</option>
                            <option value="alpha-a-z">
                              Alphabetically, A-Z
                            </option>
                            <option value="alpha-z-a">
                              Alphabetically, Z-A
                            </option>
                            <option value="price-l-h">Price, Low-High</option>
                            <option value="price-h-l">Price, High-Low</option>
                            <option value="prod-l-f">Latest first</option>
                            <option value="prod-r-f">Best review first</option>
                            <option value="manual-system">Manual system</option>
                          </select>
                        </div>
                      </li>
                    </ul>
                  </div>

                  {/* Product listing */}
                  {loading ? (
                    <div className="row">
                      {products.map(renderProduct)}
                      {Array(6)
                        .fill(0)
                        .map((_, idx) => (
                          <div
                            className="col-lg-4 col-md-6 col-12"
                            key={idx.toLocaleString()}
                          >
                            <div className="poroduct_item_block">
                              <div
                                className="product_img_block"
                                style={{
                                  height: "180px",
                                  position: "relative",
                                  borderRadius: "8px",
                                  overflow: "hidden",
                                }}
                              >
                                <Skeleton
                                  height="100%"
                                  width="100%"
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                  }}
                                />
                              </div>
                              <div
                                className="product_details_block"
                                style={{
                                  textAlign: "center",
                                  marginTop: "10px",
                                  marginBottom: "10px",
                                }}
                              >
                                <Skeleton height={15} width="90%" />
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: "10px",
                                    marginTop: "10px",
                                  }}
                                >
                                  <Skeleton height={30} width={100} />
                                  <Skeleton height={30} width={100} />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : products.length === 0 ? (
                    <div className="no-product-wrapper">
                      <img
                        src={notFoundImg}
                        alt="No products"
                        className="no-product-img"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="row">{products.map(renderProduct)}</div>
                      <Pagination />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Category;
